// CARROSSEL DO FLY NO INSTAGRAM — a previa e o botao de publicar (pedido dele, 23/09/2026).
//
// A campanha vira um carrossel de videos 4:5 (capa, produtos de 2 em 2 empilhados, fechamento).
// O sistema GERA os videos e sugere a legenda; ele assiste, edita a legenda e APERTA Publicar.
// ⛔ Publicar e' dele: o Instagram nao apaga post pelo sistema, so' pelo aplicativo.
// ⛔ Nada aqui nasce marcado nem dispara sozinho — abrir o modal nao gera nem publica nada.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, ExternalLink, RefreshCw, Instagram, AlertTriangle } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';

type Status = 'gerando' | 'pronto' | 'erro' | 'publicando' | 'publicado' | 'erro_publicar';

interface Resposta {
  /** So' quando publicado: a Meta diz se o post ainda existe. `null` = nao se aplica ou nao sei. */
  post_apagado: boolean | null;
  campanha: { id: number; nome: string; somente_texto: boolean; produtos: number };
  estado: null | {
    status: Status;
    itens: number;
    progresso: number;
    legenda: string | null;
    erro: string | null;
    permalink: string | null;
    gerado_em: string | null;
    publicado_em: string | null;
    publicado_por: string | null;
    etapa: string | null;
    /** 0-100 enquanto publica; nulo fora disso. */
    etapa_pct: number | null;
  };
  videos: string[];
}

const LIMITE_LEGENDA = 2200;

function dataHora(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function ModalCarrosselInstagram({ campanhaId, onFechar, onMudou }: {
  campanhaId: number;
  onFechar: () => void;
  /** Avisa a lista de campanhas que o estado mudou (o botao da linha mostra "publicado"). */
  onMudou?: () => void;
}) {
  const toast = useToast();
  const dialog = useDialog();
  const [dados, setDados] = useState<Resposta | null>(null);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [legenda, setLegenda] = useState('');
  const [enviando, setEnviando] = useState(false);
  // A legenda do servidor so' entra no campo quando uma geracao NOVA termina — senao o poll
  // apagaria o que ele esta digitando.
  const legendaDeQuemGerou = useRef<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get<Resposta>('/api/carrossel-fly/' + campanhaId);
      setDados(r);
      setErroCarga(null);
      const marca = r.estado?.gerado_em ?? null;
      if (r.estado?.legenda != null && marca !== legendaDeQuemGerou.current) {
        legendaDeQuemGerou.current = marca;
        setLegenda(r.estado.legenda);
      }
    } catch (e) {
      setErroCarga((e as Error).message || 'não consegui ler o estado do carrossel');
    }
  }, [campanhaId]);

  useEffect(() => { void carregar(); }, [carregar]);

  const status = dados?.estado?.status ?? null;
  const emCurso = status === 'gerando' || status === 'publicando';
  const statusAnterior = useRef<Status | null>(null);
  useEffect(() => {
    if (statusAnterior.current && statusAnterior.current !== status) onMudou?.();
    statusAnterior.current = status;
  }, [status, onMudou]);

  useEffect(() => {
    if (!emCurso) return;
    const t = setInterval(() => { void carregar(); }, 4000);
    return () => clearInterval(t);
  }, [emCurso, carregar]);

  async function gerar() {
    setEnviando(true);
    try {
      await api.post('/api/carrossel-fly/' + campanhaId + '/gerar', {});
      await carregar();
    } catch (e) {
      toast.erro((e as Error).message || 'não consegui começar a gerar');
    } finally {
      setEnviando(false);
    }
  }

  // O post foi apagado do Instagram: reabre a linha E gera os videos de novo (23/09/2026).
  // ⛔ Os dois passos num gesto so': com "Gerar de novo" separado, ele republicou os videos VELHOS
  // (a capa vazia que motivou apagar) — o botao Publicar ja estava aceso e o passo do meio sumiu.
  async function reabrir() {
    setEnviando(true);
    try {
      await api.post('/api/carrossel-fly/' + campanhaId + '/reabrir', {});
      await api.post('/api/carrossel-fly/' + campanhaId + '/gerar', {});
      await carregar();
    } catch (e) {
      toast.erro((e as Error).message || 'não consegui liberar a nova publicação');
    } finally {
      setEnviando(false);
    }
  }

  async function publicar() {
    const ok = await dialog.confirmar(
      'O carrossel (' + (dados?.videos.length ?? 0) + ' vídeos) vai para o perfil @fiodecobreme com a legenda que está na tela. '
      + 'Depois de publicado, só dá para apagar pelo aplicativo do Instagram.',
      { titulo: 'Publicar no Instagram agora?', textoConfirmar: 'Publicar no Instagram' },
    );
    if (!ok) return;
    setEnviando(true);
    try {
      await api.post('/api/carrossel-fly/' + campanhaId + '/publicar', { legenda });
      await carregar();
    } catch (e) {
      toast.erro((e as Error).message || 'não consegui publicar');
    } finally {
      setEnviando(false);
    }
  }

  const estado = dados?.estado ?? null;
  const podePublicar = (status === 'pronto' || status === 'erro_publicar') && legenda.trim().length > 0
    && legenda.length <= LIMITE_LEGENDA && !enviando;
  const motivoSemPublicar = status !== 'pronto' && status !== 'erro_publicar' ? null
    : legenda.trim().length === 0 ? 'Escreva a legenda para publicar.'
      : legenda.length > LIMITE_LEGENDA ? 'A legenda passou de 2.200 caracteres (limite do Instagram).'
        : null;

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo={'Carrossel no Instagram' + (dados ? ' — ' + dados.campanha.nome : '')}
      largura={1100}
      rodape={
        <div className="flex items-center justify-end gap-2 w-full">
          {motivoSemPublicar && <span className="text-xs mr-auto" style={{ color: 'var(--text-muted)' }}>{motivoSemPublicar}</span>}
          <button type="button" className="btn-secundario" onClick={onFechar}>Fechar</button>
          {(status === 'pronto' || status === 'erro_publicar') && (
            <button type="button" className="btn-primario flex items-center gap-1" disabled={!podePublicar} onClick={() => void publicar()}>
              <Instagram size={14} /> Publicar no Instagram
            </button>
          )}
        </div>
      }
    >
      {erroCarga && (
        <div className="text-sm flex items-center gap-2 mb-2" style={{ color: 'var(--text-muted)' }}>
          <AlertTriangle size={14} /> {erroCarga}
        </div>
      )}
      {!dados ? (
        !erroCarga && <div className="flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Carregando…</div>
      ) : dados.campanha.somente_texto ? (
        <div className="text-sm">Esta campanha é <strong>Somente Texto</strong> — não tem arte para virar carrossel.</div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {dados.campanha.produtos} produtos → capa, telas de 2 em 2 e fechamento, um vídeo 4:5 por tela, só com música.
          </div>

          {(status === null || status === 'erro') && (
            <div className="flex flex-col gap-2 items-start">
              {status === 'erro' && estado?.erro && (
                <div className="text-sm flex items-center gap-2"><AlertTriangle size={14} /> Não consegui gerar: {estado.erro}</div>
              )}
              <button type="button" className="btn-primario flex items-center gap-1" disabled={enviando} onClick={() => void gerar()}>
                {enviando ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Gerar os vídeos
              </button>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Leva uns 5 minutos. Os preços saem os de agora. Nada é publicado nesta etapa.
              </span>
            </div>
          )}

          {status === 'gerando' && (
            <div className="flex items-center gap-2 text-sm">
              <Loader2 size={16} className="animate-spin" />
              Gerando o vídeo {Math.min((estado?.progresso ?? 0) + 1, estado?.itens ?? 1)} de {estado?.itens} — pode fechar esta janela, a geração continua.
            </div>
          )}

          {status === 'publicando' && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-sm">
                <Loader2 size={16} className="animate-spin" />
                Publicando no Instagram{estado?.etapa ? ' — ' + estado.etapa : ''}… (leva uns 2 minutos)
              </div>
              {/* A barra (pedido dele, 23/09/2026). O percentual vem do servidor, pesado pelo tempo
                  real de cada etapa. ⚠️ Cores FIXAS, nao do tema: trilho e preenchimento de variaveis
                  diferentes nao garantem contraste entre si ([DA-678]). */}
              <div className="flex items-center gap-2">
                <div
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={estado?.etapa_pct ?? 0}
                  style={{ flex: 1, height: 14, borderRadius: 999, backgroundColor: '#cbd5e1', overflow: 'hidden' }}
                >
                  <div style={{
                    width: Math.max(2, estado?.etapa_pct ?? 0) + '%', height: '100%', borderRadius: 999,
                    backgroundColor: '#1d4ed8', transition: 'width 1.5s ease-out',
                  }} />
                </div>
                <span className="text-sm tabular-nums font-semibold" style={{ minWidth: 44, textAlign: 'right' }}>
                  {estado?.etapa_pct ?? 0}%
                </span>
              </div>
            </div>
          )}

          {status === 'publicado' && (
            <div className="text-sm flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 rounded" style={{ backgroundColor: '#fde68a', color: '#000' }}>Publicado</span>
              em {dataHora(estado?.publicado_em ?? null)}{estado?.publicado_por ? ' por ' + estado.publicado_por : ''}
              {estado?.permalink && dados.post_apagado !== true && (
                <a href={estado.permalink} target="_blank" rel="noreferrer" className="flex items-center gap-1 underline">
                  Ver no Instagram <ExternalLink size={12} />
                </a>
              )}
            </div>
          )}

          {status === 'publicado' && dados.post_apagado === true && (
            <div className="flex items-center gap-2 flex-wrap text-sm">
              <AlertTriangle size={14} /> Este post foi apagado do Instagram.
              <button type="button" className="btn-primario text-xs flex items-center gap-1" disabled={enviando} onClick={() => void reabrir()}>
                {enviando ? <Loader2 size={12} className="animate-spin" /> : <Instagram size={12} />} Publicar de novo
              </button>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Gera os vídeos de novo (uns 3 minutos, preços de agora) — depois você revisa e aperta Publicar.
              </span>
            </div>
          )}

          {status === 'erro_publicar' && estado?.erro && (
            <div className="text-sm flex items-center gap-2"><AlertTriangle size={14} /> A publicação falhou: {estado.erro}</div>
          )}

          {dados.videos.length > 0 && (
            <>
              <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
                {dados.videos.map((url, i) => (
                  <div key={url} className="flex flex-col gap-1">
                    <video src={url} controls preload="metadata" playsInline
                      style={{ width: '100%', aspectRatio: '4 / 5', backgroundColor: '#000', borderRadius: 6 }} />
                    <span className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                      {i === 0 ? 'Capa' : i === dados.videos.length - 1 ? 'Fechamento' : 'Tela ' + (i + 1)}
                    </span>
                  </div>
                ))}
              </div>
              {(status === 'pronto' || status === 'erro_publicar') && (
                <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                  Vídeos gerados em {dataHora(estado?.gerado_em ?? null)} — os preços são os daquele momento.
                  <button type="button" className="btn-secundario text-xs flex items-center gap-1" disabled={enviando} onClick={() => void gerar()}>
                    <RefreshCw size={12} /> Gerar de novo
                  </button>
                </div>
              )}
              <label className="flex flex-col gap-1">
                <span className="text-sm font-semibold">Legenda do post</span>
                <textarea
                  value={legenda}
                  onChange={(e) => setLegenda(e.target.value)}
                  readOnly={status !== 'pronto' && status !== 'erro_publicar'}
                  rows={12}
                  // ⛔ `no-upper` — a legenda SAI PRA FORA; mesma escotilha do texto da campanha.
                  className="w-full px-2 py-1 rounded border no-upper"
                  style={{ backgroundColor: 'var(--panel-bg)', borderColor: 'var(--border)', color: 'var(--text)', resize: 'vertical' }}
                />
                <span className="text-xs text-right tabular-nums" style={{ color: legenda.length > LIMITE_LEGENDA ? '#dc2626' : 'var(--text-muted)' }}>
                  {legenda.length.toLocaleString('pt-BR')} / 2.200
                </span>
              </label>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
